import { InfoCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { useResendConfirmation } from "@entities/brief/lib/hooks/useResendConfirmation";
import { useSkipConfirmationMail } from "@entities/brief/lib/hooks/useSkipConfirmationMail";
import { BriefConfirmation } from "@features/BriefConfirmation/BriefConfirmation";
import { ToastService } from "@shared/lib/services";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button, Modal, Tag } from "@shared/ui";

import { useDeclineInfluencer } from "@entities/brief/lib/hooks/useDeclineInfluencer";
import { Dropdown, Flex, Input, Tooltip } from "antd";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

type ClosureDetailActionsCellProps = {
  record: IBriefInfluencerV2;
  briefId: string;
  onActionSuccess: () => void;
};

export function ClosureDetailActionsCell({
  record,
  briefId,
  onActionSuccess,
}: ClosureDetailActionsCellProps) {
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const { resendConfirmation, isResending, isResent, variables } =
    useResendConfirmation(briefId);
  const [reason, setReason] = useState("");

  const {
    decline: declineCreator,
    isDeclining: isDecliningCreator,
    isDeclined: isDeclinedCreator,
  } = useDeclineInfluencer(briefId);

  const {
    skipConfirmationMail,
    isSkippingConfirmationMail,
    isSkippedConfirmationMail,
  } = useSkipConfirmationMail(briefId);

  useEffect(() => {
    if (isResent) {
      ToastService.success("Confirmation resent successfully");
      onActionSuccess();
    }
  }, [isResent]);

  useEffect(() => {
    if (isSkippedConfirmationMail) {
      ToastService.success("Confirmation skipped successfully");
      setIsSkipModalOpen(false);
      onActionSuccess();
    }
  }, [isSkippedConfirmationMail]);
  useEffect(() => {
    if (isDeclinedCreator) {
      ToastService.success("Creator declined successfully");
      setIsDeclineModalOpen(false);
      onActionSuccess();
      setReason("");
    }
  }, [isDeclinedCreator]);

  const skipMenuItems = [
    {
      key: "skip-confirmation",
      label: "Skip confirmation",
      onClick: () => setIsSkipModalOpen(true),
    },
  ];
  const declineMenuItems = [
    {
      key: "decline-creator",
      label: "Decline creator",
      onClick: () => setIsDeclineModalOpen(true),
    },
  ];

  const handleSkipConfirm = () => {
    skipConfirmationMail({ briefInfluencerIds: [record._id] });
  };

  const handleDecline = () => {
    declineCreator({ reason, briefInfluencerId: record._id });
  };

  const skipModal = (
    <Modal
      title="Skip confirmation"
      open={isSkipModalOpen}
      onCancel={() => setIsSkipModalOpen(false)}
      onOk={handleSkipConfirm}
      confirmLoading={isSkippingConfirmationMail}
      okText="Skip"
    >
      <p>Are you sure you want to skip confirmation for this creator?</p>
    </Modal>
  );

  const declineModal = (
    <Modal
      open={isDeclineModalOpen}
      title="Confirm"
      onCancel={() => setIsDeclineModalOpen(false)}
      onOk={handleDecline}
      okText="Continue"
      confirmLoading={isDecliningCreator}
      okButtonProps={{ disabled: !reason }}
    >
      <div style={{ marginBottom: 16 }}>
        Are you sure you want to drop the creator?
      </div>
      <div style={{ marginBottom: 8 }}>Enter the reason for Drop</div>
      <Input
        placeholder="Reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );

  const actionsMenu = (showSkipButton: boolean) => (
    <Dropdown
      menu={{
        items: [...(showSkipButton ? skipMenuItems : []), ...declineMenuItems],
      }}
      trigger={["click"]}
    >
      <Button icon={<MoreOutlined />} type="text" size="small" />
    </Dropdown>
  );

  if (record.isCreatorDeclined || record.isBrandDeclined) {
    return (
      <Tooltip
        title={
          record.isCreatorDeclined
            ? record.creatorDeclinedReason
            : record.brandDeclinedReason
        }
      >
        <Button
          type="link"
          icon={<InfoCircleOutlined />}
          style={{ color: "var(--info-color)" }}
          className={styles.tooltipButton}
          size="small"
        >
          {record.isCreatorDeclined ? "Creator Declined" : "Brand Declined"}
        </Button>
      </Tooltip>
    );
  }

  if (record.isConfirmationSent && record.isConfirmed) {
    return (
      <Tag color="success" className={styles.allDoneTag}>
        All done
      </Tag>
    );
  }

  if (record.isConfirmationSent && !record.isConfirmed) {
    return (
      <>
        <Flex gap={4} justify="flex-end" align="center">
          <Button
            type="primary"
            size="small"
            loading={isResending && variables?.[0] === record._id}
            onClick={() => resendConfirmation([record._id])}
          >
            Resend confirmation mail
          </Button>
          {actionsMenu(false)}
          {declineModal}
        </Flex>
      </>
    );
  }

  if (!record.isConfirmationSent) {
    return (
      <>
        <Flex gap={4} justify="flex-end" align="center">
          <Button
            type="primary"
            size="small"
            onClick={() => setIsSendModalOpen(true)}
          >
            Send confirmation mail
          </Button>
          {actionsMenu(true)}
        </Flex>
        <Modal
          title="Send confirmation"
          open={isSendModalOpen}
          onCancel={() => setIsSendModalOpen(false)}
          footer={null}
          width={1300}
          destroyOnClose
        >
          {isSendModalOpen ? (
            <BriefConfirmation
              briefInfluencer={record}
              onClose={() => {
                setIsSendModalOpen(false);
                onActionSuccess();
              }}
            />
          ) : null}
        </Modal>
        {skipModal}
        {declineModal}
      </>
    );
  }

  return null;
}
